import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { DesignTool, designTargetSchemaFields } from './designTargeting';
import type { TechniqueAssignment } from './skills/suggestTechniques';
import { assignmentFor, fallbackAssignment, shortlist } from './skills/suggestTechniques';
import { chooseTechniques } from './skills/chooseTechniques';

import { markScenesBusy } from 'js/src/data/agent-activity';
import {
    getAssignedTechniques,
    getEarlierScenePlans,
    setHtmlDesignPlan,
} from 'js/src/data/html-design';
import { setSceneDurationUserOverride } from 'js/src/data/user-overrides';
import { setSceneDuration } from 'js/src/luminary/utils';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';

export type { ScenePlan } from 'js/src/data/html-design';

export const writePlanSchema = z.object({
    ...designTargetSchemaFields,
    sceneDurationMillis: z
        .number()
        .describe(
            'How long the whole scene runs, in milliseconds. You pace the ' +
                'graphic within it when you author the timeline.',
        ),
    scenePlan: z
        .object({
            text: z
                .array(z.string())
                .describe(
                    'The exact words of every text block the graphic shows — ' +
                        'one entry per block, written out, not described. Empty if ' +
                        'the graphic shows no text. Read the list back before ' +
                        'submitting: no figure and no claim may appear in more ' +
                        'than one entry.',
                ),
            graphics: z
                .array(z.string())
                .describe(
                    'Each non-text element, one per entry (e.g. "person icon", ' +
                        '"filled person icon").',
                ),
            layout: z
                .string()
                .describe('Where those elements sit, e.g. "text on left, graphic on ' + 'right".'),
        })
        .describe('What the graphic is, described once for the whole scene.'),
});
export type WritePlanArgs = z.infer<typeof writePlanSchema>;

const VOICEOVER_TOLERANCE_RATIO = 0.15;
const VOICEOVER_TOLERANCE_FLOOR_MS = 500;

function voiceoverTolerance(sceneDurationMs: number): number {
    return Math.max(VOICEOVER_TOLERANCE_FLOOR_MS, sceneDurationMs * VOICEOVER_TOLERANCE_RATIO);
}

// The agent is told to branch on `approved`, so a rejection has to arrive in that shape rather than
// as prose it would have to interpret.
function denied(reason: string): EnhancedToolResult {
    return { result: JSON.stringify({ approved: false, reason }), isSuccess: false };
}

export class WritePlanTool extends DesignTool<typeof writePlanSchema> {
    protected override readonly schema = writePlanSchema;
    protected override readonly toolName = TOOL_NAMES.writePlan;
    protected override readonly toolDescription =
        'Submit your plan for the graphic on a scene and have it reviewed. You MUST call ' +
        `this for a scene and get an approved plan before you call ${TOOL_NAMES.writeHtmlAsset} ` +
        'for that scene. ' +
        'Returns {"approved": true|false, "notes": "...", "reason": "...", "techniques": {...}}. When ' +
        'approved is false the plan is rejected — read "reason", revise, and call again; ' +
        'do not start authoring. When approved is true, "notes" may carry advice worth ' +
        'applying (often blank), and "techniques.required" names the one or two design & motion ' +
        'skills this graphic is to be built from — an assignment, not a list of options. ' +
        `${TOOL_NAMES.loadSkill} each of them, implement them, and only then write the HTML with ` +
        `${TOOL_NAMES.writeHtmlAsset}. ` +
        'sceneDurationMillis is how long the whole scene should play.';

    public getToolDisplayName(): string {
        return 'Planning the graphic';
    }

    public get statusIcon(): string {
        return 'draft';
    }

    public async execute(
        args: WritePlanArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveTarget(args.scene_id);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { scene } = resolved;

        const { sceneDurationMillis } = args;
        if (!Number.isFinite(sceneDurationMillis) || sceneDurationMillis <= 0) {
            return denied('sceneDurationMillis must be a positive number of milliseconds.');
        }

        if (isVoicedrivenScene(scene)) {
            const drift = Math.abs(sceneDurationMillis - scene.duration);
            if (drift > voiceoverTolerance(scene.duration)) {
                return denied(
                    `This scene's duration is fixed at ${scene.duration}ms by its voiceover, ` +
                        `and the plan is built for ${sceneDurationMillis}ms. The timing isn't ` +
                        `yours to choose here — re-plan the graphic to fill ${scene.duration}ms, ` +
                        `and pass that as sceneDurationMillis. ` +
                        `If that leaves more time than the content needs, spend it developing what ` +
                        `is already on screen rather than adding to it.`,
                );
            }
        } else {
            // Mirrors set_scene_duration: mark it a user override so a later text-driven
            // recommended-duration recalc doesn't reset the value the plan was built against.
            setSceneDuration(this.luminary, scene.id, sceneDurationMillis);
            this.dispatch(
                setSceneDurationUserOverride({
                    sceneId: scene.id,
                    isAutomatic: false,
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                }),
            );
        }

        const techniques = await this.assignTechniques(args, scene.id, context);

        // After the assignment, so the store holds only earlier scenes while it is made. Remembered
        // so the composition the agent then authors can be checked against it: unplanned copy is
        // reported as a blocking finding by write_html_asset and edit_html_asset.
        this.dispatch(
            setHtmlDesignPlan({
                sceneId: scene.id,
                plan: args.scenePlan,
                techniques: techniques.required.map(({ skill }) => skill),
            }),
        );
        // An approved plan is where building starts, so the editor dims the scene from here.
        markScenesBusy(this.dispatch, [scene.id]);

        return {
            result: JSON.stringify({ approved: true, notes: '', techniques }),
            isSuccess: true,
        };
    }

    private async assignTechniques(
        args: WritePlanArgs,
        sceneId: string,
        context: ToolExecutionContext,
    ): Promise<TechniqueAssignment> {
        const state = this.getState();
        const input = {
            sceneDurationMillis: args.sceneDurationMillis,
            alreadyAssigned: getAssignedTechniques(state, sceneId),
        };
        const chosen = context.model
            ? await chooseTechniques(
                  args.scenePlan,
                  shortlist(input),
                  getEarlierScenePlans(state, sceneId),
                  context.model,
                  context.abortSignal,
              )
            : [];
        return chosen.length > 0 ? assignmentFor(chosen, input) : fallbackAssignment(input);
    }
}
